import React from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { IconButton } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowUpRight03Icon } from "@hugeicons-pro/core-stroke-standard";
import { titleCase } from "title-case";
import { TimeFrameEnum } from "enums/AnalyticsTimeFrame";
import AccountType from "enums/AccountType";
import ApplicationType from "enums/ApplicationType";
import type {
  BankAccount,
  UseGetBankAccountsResult,
} from "hooks/useGetBankAccounts";
import useCashFlowSummary from "hooks/useCashFlowSummary";
import { Center, Inline, Stack } from "components/layout/reusableLayouts";
import BalanceAmount from "components/common/BalanceAmount";
import TrendChart from "components/common/TrendChart";
import { formatCashFull } from "utils/formatCash";

interface CashFlowPanelProps {
  bankAccounts: UseGetBankAccountsResult;
  /** The account the page is scoped to, or null for every account. */
  accountId: string | null;
  timeFrame: TimeFrameEnum;
}

const PANEL_MIN_HEIGHT = 360;

const CashFlowPanel: React.FC<CashFlowPanelProps> = ({
  bankAccounts,
  accountId,
  timeFrame,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    allAccounts,
    isLoading: loadingAccounts,
    loadingBalances,
    combinedBalances,
  } = bankAccounts;
  const loading = loadingAccounts || loadingBalances;

  // TODO: multiple financial accounts are not supported; the first is the main one.
  const financialAccount = allAccounts.find(
    (account) => account.accountType === AccountType.FINANCIAL_ACCOUNT,
  );

  // An id that is no longer listed falls back to the main account rather than to "all".
  let selectedAccount: BankAccount | undefined;
  let chartAccountId = accountId ?? undefined;
  if (accountId) {
    selectedAccount =
      allAccounts.find((account) => account.id === accountId) ??
      financialAccount;
    if (selectedAccount) chartAccountId = selectedAccount.id;
  }

  const isExternalCard =
    selectedAccount?.accountType === AccountType.PLAID_EXTERNAL_CARD_ACCOUNT;
  const showChart = !accountId || (!!selectedAccount && !isExternalCard);

  // A curve nobody sees is not fetched: a BYOC account, or an account not listed yet.
  const cashFlow = useCashFlowSummary(
    timeFrame,
    chartAccountId,
    !showChart,
    true,
  );
  const isChargeAccount =
    selectedAccount?.applicationType === ApplicationType.CHARGE ||
    financialAccount?.applicationType === ApplicationType.CHARGE;

  // A nickname is runtime data, so it never goes through t().
  const accountName = (account: BankAccount) =>
    titleCase(
      (
        account.nickname ||
        account.bankName ||
        t("your main account")
      ).toLowerCase(),
    );

  const title = selectedAccount ? accountName(selectedAccount) : t("Cash Flow");

  const combinedIsZero =
    combinedBalances === "0" || combinedBalances === "$0.00";
  const balance = selectedAccount
    ? selectedAccount.availableAmount
    : loading && combinedIsZero
      ? undefined
      : combinedBalances;

  const today = new Date().toLocaleDateString(
    i18n.language === "es" ? "es-ES" : "en-US",
    { month: "long", day: "2-digit", year: "numeric" },
  );

  return (
    <Card
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        minHeight: PANEL_MIN_HEIGHT,
        padding: "var(--space-6)",
        gap: "var(--space-4)",
      }}
    >
      <Inline justify="between" align="start" wrap={false}>
        <Stack gap="var(--space-3)" sx={{ minWidth: 0 }}>
          <Typography variant="h4" component="h3">
            {title}
          </Typography>
          {isExternalCard ? null : balance ? (
            <BalanceAmount
              value={balance}
              variant="h2"
              centsSize="var(--type-size-lg)"
            />
          ) : loading ? (
            <Skeleton variant="text" width={200}>
              <Typography variant="h2">{"..."}</Typography>
            </Skeleton>
          ) : (
            <Typography variant="h2">{"..."}</Typography>
          )}
        </Stack>
        <IconButton
          variant="ghost"
          aria-label={t("View transactions")}
          onClick={() =>
            navigate("/admin/transactions", {
              state: selectedAccount
                ? { accountId: selectedAccount.id }
                : undefined,
            })
          }
        >
          <HugeiconsIcon icon={ArrowUpRight03Icon} size={20} />
        </IconButton>
      </Inline>

      <Stack gap="var(--space-1)">
        {isChargeAccount &&
          !isExternalCard &&
          financialAccount?.creditLimit && (
            <Typography
              variant="label"
              sx={{ color: "var(--color-text-tertiary)" }}
            >
              {t("Available Credit of {{limit}} limit", {
                limit: financialAccount.creditLimit,
              })}
            </Typography>
          )}
        <Typography
          variant="label"
          sx={{ color: "var(--color-text-tertiary)" }}
        >
          {today}
        </Typography>
      </Stack>

      {isExternalCard && (
        <Center sx={{ flex: 1, minWidth: 0 }}>
          <Stack gap="var(--space-4)">
            <Divider />
            <Typography
              variant="label"
              sx={{ color: "var(--color-text-tertiary)" }}
            >
              <Trans i18nKey="byoc_account_info">
                This is a BYOC (Bring Your Own Card) account. Balance is managed
                externally and not available for card spend or reimbursements
                through Outpave.
              </Trans>
            </Typography>
            <Divider />
          </Stack>
        </Center>
      )}

      {showChart && (
        <TrendChart
          series={cashFlow.series.map((point) => ({
            date: point.date,
            value: point.balance,
          }))}
          formatValue={(value) => formatCashFull(value, cashFlow.currency)}
          loading={cashFlow.loading}
        />
      )}
    </Card>
  );
};

export default CashFlowPanel;
